import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { User, Clock, AlertTriangle } from 'lucide-react'

interface HOSStatus {
  driver_id: string
  driver_name: string
  current_status: 'off_duty' | 'sleeper' | 'driving' | 'on_duty'
  drive_time_remaining: number
  duty_time_remaining: number
  cycle_time_remaining: number
  violations: number
  last_update: number
}

const statusConfig = {
  off_duty: { label: 'Off Duty', color: 'bg-gray-500', badge: 'secondary' as const },
  sleeper: { label: 'Sleeper', color: 'bg-blue-500', badge: 'secondary' as const },
  driving: { label: 'Driving', color: 'bg-green-500', badge: 'default' as const },
  on_duty: { label: 'On Duty', color: 'bg-yellow-500', badge: 'secondary' as const },
}

export function HOSDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['hos-status'],
    queryFn: async () => {
      const res = await fetch('/api/eld/hos/status')
      if (!res.ok) throw new Error('Failed to fetch HOS status')
      return res.json() as Promise<{ drivers: HOSStatus[] }>
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const formatTime = (minutes: number | null) => {
    if (minutes === null || minutes === undefined) return 'N/A'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }

  const getTimePercentage = (remaining: number, total: number) => {
    return (remaining / total) * 100
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Hours of Service (HOS) Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading HOS data...
          </div>
        ) : !data?.drivers || data.drivers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No active drivers found
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.drivers.map((driver) => {
              const config = statusConfig[driver.current_status]
              const hasViolations = driver.violations > 0
              const driveTimeWarning = (driver.drive_time_remaining || 0) < 60
              const dutyTimeWarning = (driver.duty_time_remaining || 0) < 60

              return (
                <Card key={driver.driver_id} className={hasViolations ? 'border-red-500' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <CardTitle className="text-base">{driver.driver_name}</CardTitle>
                      </div>
                      {hasViolations && (
                        <Badge variant="destructive" className="ml-2">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {driver.violations}
                        </Badge>
                      )}
                    </div>
                    <Badge variant={config.badge} className="w-fit mt-2">
                      {config.label}
                    </Badge>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Drive Time */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Drive Time</span>
                        <span className={driveTimeWarning ? 'text-red-500 font-medium' : ''}>
                          {formatTime(driver.drive_time_remaining)}
                        </span>
                      </div>
                      <Progress
                        value={getTimePercentage(driver.drive_time_remaining || 0, 660)}
                        className="h-2"
                      />
                    </div>

                    {/* Duty Time */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Duty Time</span>
                        <span className={dutyTimeWarning ? 'text-red-500 font-medium' : ''}>
                          {formatTime(driver.duty_time_remaining)}
                        </span>
                      </div>
                      <Progress
                        value={getTimePercentage(driver.duty_time_remaining || 0, 840)}
                        className="h-2"
                      />
                    </div>

                    {/* Cycle Time */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Cycle Time</span>
                        <span>{formatTime(driver.cycle_time_remaining)}</span>
                      </div>
                      <Progress
                        value={getTimePercentage(driver.cycle_time_remaining || 0, 4200)}
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
